import React, { useMemo, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { BlockData, BlockType } from '../config/wordOverlayConfig';
import { BLOCK_TYPES } from '../config/wordOverlayConfig';
import { useWordDataContext } from '../contexts/WordDataContext';

interface BlockToolbarProps {
  block: BlockData;
  pageIndex: number;
}

// Helper function to parse block ID
const parseBlockId = (blockId: string | number): number => {
  return typeof blockId === 'string' ? parseInt(blockId, 10) : blockId;
};

const BlockToolbar: React.FC<BlockToolbarProps> = ({ block, pageIndex }) => {
  const { actions } = useWordDataContext();
  
  // Memoize blockId to avoid repeated parsing
  const blockId = useMemo(() => parseBlockId(block.blockId), [block.blockId]);
  
  const handleBlockTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newBlockType = e.target.value as BlockType;
    actions.updateBlockMetadata(pageIndex, blockId, { blockType: newBlockType });
  }, [pageIndex, blockId, actions]);
  
  const handleAddWordClick = useCallback(() => {
    // Add a new word to the top-left of the block
    const newBoundingBox = {
      x: block.boundingBox.x,
      y: block.boundingBox.y,
      width: 100,  // Default width for better visibility
      height: 50  // Default height for better visibility
    };
    
    actions.addWordToBlock(pageIndex, blockId, '-', newBoundingBox);
  }, [pageIndex, block, blockId, actions]);
  
  const handleReadBlockToggle = useCallback(() => {
    const currentReadBlock = block.readBlock ?? true; // Default to true if undefined
    actions.updateBlockMetadata(pageIndex, blockId, { readBlock: !currentReadBlock });
  }, [pageIndex, block, blockId, actions]);

  const handleDeleteBlock = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this block?')) {
      actions.deleteBlock(pageIndex, blockId);
    }
  }, [pageIndex, blockId, actions]);
  
  return (
    <div className="block-toolbar">
      {/* Add Word Icon */}
      <button 
        className="block-toolbar-button block-toolbar-button-add"
        title="Add word"
        aria-label="Add word"
        onClick={handleAddWordClick}
      >
        <Plus size={14} strokeWidth={2.5} />
      </button>

      {/* Block Type Selector */}
      <select
        value={block.blockType || ''}
        onChange={handleBlockTypeChange}
        className="toolbar-select"
        onClick={(e) => e.stopPropagation()}
        aria-label="Block type"
      >
        <option value="">-- No Block Type --</option>
        {BLOCK_TYPES.map(type => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>

      {/* Read Block Toggle */}
      <div className="block-toolbar-read-toggle">
        <label className="compact-toggle-label">
          <span className="compact-toggle-text">Read</span>
          <div className="compact-toggle-switch">
            <input
              type="checkbox"
              checked={block.readBlock !== false}
              onChange={handleReadBlockToggle}
              className="compact-toggle-input"
              id={`read-toggle-${blockId}`}
              onClick={(e) => e.stopPropagation()}
            />
            <span className="compact-toggle-slider"></span>
          </div>
        </label>
      </div>

      {/* Delete Block Button */}
      <button 
        className="block-toolbar-button block-toolbar-button-delete"
        title="Delete block"
        aria-label="Delete block"
        onClick={handleDeleteBlock}
      >
        <Trash2 size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
};

export default BlockToolbar;
